import React, { useEffect, useRef, useState } from 'react';

const GooglePlacesInput = ({
  value,
  onChange,
  placeholder = '',
  apiKey,
  hasError = false,
  onPlaceSelect
}) => {
  const [query, setQuery] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const autocompleteSessionTokenRef = useRef(
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`
  );

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const requestPredictions = async (text) => {
    if (!apiKey || text.trim().length < 2) {
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    setLoadingPredictions(true);
    try {
      const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'suggestions.placePrediction.place,suggestions.placePrediction.placeId,suggestions.placePrediction.text'
        },
        body: JSON.stringify({
          input: text,
          sessionToken: autocompleteSessionTokenRef.current
        })
      });

      const data = await response.json();
      const placePredictions = Array.isArray(data?.suggestions)
        ? data.suggestions
            .map((item) => item?.placePrediction)
            .filter(Boolean)
        : [];

      if (!placePredictions.length) {
        setPredictions([]);
        setIsOpen(false);
        return;
      }

      setPredictions(placePredictions);
      setIsOpen(true);
    } catch (error) {
      setPredictions([]);
      setIsOpen(false);
    } finally {
      setLoadingPredictions(false);
    }
  };

  const selectPrediction = async (prediction) => {
    const predictionPlaceId =
      prediction?.placeId ||
      (typeof prediction?.place === 'string' && prediction.place.startsWith('places/')
        ? prediction.place.replace('places/', '')
        : '');

    const fallbackName = prediction?.text?.text || '';

    if (!apiKey || !predictionPlaceId) {
      setQuery(fallbackName);
      onChange(fallbackName);
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    try {
      const response = await fetch(`https://places.googleapis.com/v1/places/${encodeURIComponent(predictionPlaceId)}`, {
        headers: {
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': '*'
        }
      });
      const placeDetails = await response.json();
      const placeName = placeDetails?.displayName?.text || fallbackName;
      console.log('Selected place object:', placeDetails);
      setQuery(placeName);
      onChange(placeName);
      if (onPlaceSelect) {
        onPlaceSelect(placeDetails);
      }
    } catch (error) {
      setQuery(fallbackName);
      onChange(fallbackName);
    } finally {
      autocompleteSessionTokenRef.current =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      setPredictions([]);
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setQuery(nextValue);
    onChange(nextValue);
    setActiveIndex(-1);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      requestPredictions(nextValue);
    }, 250);
  };

  const handleKeyDown = (event) => {
    if (!isOpen || predictions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0) {
        event.preventDefault();
        selectPrediction(predictions[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (predictions.length > 0) {
            setIsOpen(true);
          }
        }}
        className={`border p-2 w-full rounded ${hasError ? 'border-red-500' : 'border-gray-300'}`}
        placeholder={placeholder}
      />
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded border border-gray-200 bg-white shadow-md max-h-60 overflow-auto">
          {predictions.map((prediction, index) => (
            <button
              key={prediction.placeId || prediction.place}
              type="button"
              onClick={() => selectPrediction(prediction)}
              className={`w-full px-3 py-2 text-left text-sm ${
                activeIndex === index ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
              }`}
            >
              {prediction.text?.text || ''}
            </button>
          ))}
        </div>
      )}
      {loadingPredictions && (
        <div className="mt-1 text-xs text-gray-500">Loading place suggestions...</div>
      )}
    </div>
  );
};

export default GooglePlacesInput;
